import { useMemo } from 'react';

import {
  type ExternalTransactionBody,
  formatExternalStreetAddress,
  getRooms,
  getSurface,
  isExternalTransactionSource,
} from '@realadvisor/common-types';

import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { usePropertyTypes } from '@modules/_shared/providers/PropertyTypesProvider';
import { toHumanReadableDate } from '@modules/_shared/utils/formatting';
import type {
  CmaPropertyTransaction,
  MergedTransactionRow,
} from '@modules/reports/cma-reports/step-transaction/types';
import { useLocale } from '@shared/hooks/locale';
import {
  Currencies,
  type Currency,
  type IntlLocale,
  getCurrencyByCountryCode,
} from '@shared/LocaleContext';
import { formatPrice } from '@shared/utils/format-price';

const isCurrency = (value: string | null | undefined): value is Currency =>
  (Currencies as readonly string[]).includes(value ?? '');

export type PresentationInput = MergedTransactionRow | CmaPropertyTransaction;

export type TransactionPresentation = {
  source: PresentationInput['source'];
  locality: string | null;
  postcode: string | null;
  streetLabel: string | null;
  propertyTypeLabel: string | null;
  rooms: number | null;
  roomsLabel: string | null;
  surface: number | null;
  surfaceLabel: string | null;
  formattedDate: string | null;
  distance: number | null;
  priceLabel: string | null;
  pricePerSqmLabel: string | null;
};

export type PresentationContext = {
  t: (key: string) => string;
  locale: IntlLocale;
  tenantCountryCode: string | null;
  isTenantSpanish: boolean;
  getPropertyTypeLabel: (name: string | null) => string | null;
};

export const computeTransactionPresentation = (
  tx: PresentationInput,
  ctx: PresentationContext,
): TransactionPresentation => {
  const {
    t,
    locale,
    tenantCountryCode,
    isTenantSpanish,
    getPropertyTypeLabel,
  } = ctx;
  const isExternal = isExternalTransactionSource(tx.source);
  const data = tx.transaction;
  const externalData = isExternal
    ? (data as ExternalTransactionBody | null)
    : null;
  let currency = getCurrencyByCountryCode(tenantCountryCode ?? 'CH');
  if (isExternal) {
    currency = isCurrency(externalData?.currency)
      ? externalData.currency
      : getCurrencyByCountryCode(externalData?.country_code ?? 'FR');
  }
  const isSpanish = isExternal
    ? externalData?.country_code === 'ES'
    : isTenantSpanish;

  const primarySurface = getSurface(data, isSpanish);
  const surface =
    primarySurface != null && primarySurface > 0 ? primarySurface : null;

  const roomsRaw = getRooms(data, isSpanish);
  const rooms = roomsRaw != null && roomsRaw > 0 ? roomsRaw : null;
  const propertyType = getPropertyTypeLabel(data?.property_type_name ?? null);

  const priceRaw = data?.purchase_price ?? null;
  const price = priceRaw != null && priceRaw > 0 ? priceRaw : null;
  const pricePerSqm =
    price != null && surface != null ? Math.round(price / surface) : null;

  const date = data?.signature_date ?? null;
  const formattedDate =
    typeof date === 'string' && date !== ''
      ? toHumanReadableDate(locale, date, { month: 'long', year: 'numeric' })
      : null;

  const distance =
    typeof tx.distance_in_meters === 'number' &&
    Number.isFinite(tx.distance_in_meters) &&
    tx.distance_in_meters >= 0
      ? tx.distance_in_meters
      : null;

  const streetLabel =
    isExternal && data != null
      ? formatExternalStreetAddress({
          ...(data as Parameters<typeof formatExternalStreetAddress>[0]),
          country_code: externalData?.country_code ?? tenantCountryCode,
        })
      : null;

  return {
    source: tx.source,
    locality: data?.locality ?? null,
    postcode: data?.postcode ?? null,
    streetLabel,
    propertyTypeLabel: propertyType,
    rooms,
    roomsLabel: rooms != null ? `${rooms} ${t('Rooms')}` : null,
    surface,
    surfaceLabel: surface != null ? `${surface} m²` : null,
    formattedDate,
    distance,
    priceLabel: price != null ? formatPrice(price, locale, currency) : null,
    pricePerSqmLabel:
      pricePerSqm != null
        ? `${formatPrice(pricePerSqm, locale, currency)} / m²`
        : null,
  };
};

export const usePresentationContext = (): PresentationContext => {
  const { t, locale } = useLocale();
  const { getPropertyTypeLabel } = usePropertyTypes();
  const { me } = useAppData();
  const tenantCountryCode = me?.tenant?.country_code ?? null;
  const isTenantSpanish = tenantCountryCode === 'ES';
  return useMemo(
    () => ({
      t,
      locale,
      tenantCountryCode,
      isTenantSpanish,
      getPropertyTypeLabel,
    }),
    [t, locale, tenantCountryCode, isTenantSpanish, getPropertyTypeLabel],
  );
};

export const useTransactionPresentation = (
  tx: PresentationInput,
): TransactionPresentation => {
  const ctx = usePresentationContext();
  return useMemo(() => computeTransactionPresentation(tx, ctx), [tx, ctx]);
};
